import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { formatContactsSyncTime } from "@/pages/billing/v2/tabs/overview/formatContactsSyncTime";

const styles = {
  field: { position: "relative" },
  pendingField: {
    position: "relative",
    "& > span:last-child": { visibility: "hidden" },
  },
  skeleton: { position: "absolute", inset: 0 },
  root: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "24px",
    rowGap: "4px",
    marginTop: "8px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface ContactsFreshnessProps {
  lastSuccessfulSyncAt: string | null;
  dataThroughDate: string | null;
  isPending?: boolean;
}

export const ContactsFreshness: React.FC<ContactsFreshnessProps> =
  function ContactsFreshness({
    lastSuccessfulSyncAt,
    dataThroughDate,
    isPending = false,
  }) {
    const { t } = useTranslation();

    if (
      !isPending &&
      lastSuccessfulSyncAt === null &&
      dataThroughDate === null
    ) {
      return null;
    }

    // Hidden date-shaped text lets the initial skeleton wrap like the loaded fields.
    return (
      <Box sx={styles.root} aria-busy={isPending}>
        {(isPending || lastSuccessfulSyncAt !== null) && (
          <Box sx={isPending ? styles.pendingField : styles.field}>
            {isPending && <Skeleton variant="rounded" sx={styles.skeleton} />}
            <Typography variant="body" as="span" aria-hidden={isPending}>
              {t("billing.uniqueContacts.lastSuccessfulSync", {
                defaultValue: "Latest data sync: {{time}}",
                time:
                  lastSuccessfulSyncAt === null
                    ? "0000/00/00 00:00"
                    : formatContactsSyncTime(lastSuccessfulSyncAt),
              })}
            </Typography>
          </Box>
        )}
        {(isPending || dataThroughDate !== null) && (
          <Box sx={isPending ? styles.pendingField : styles.field}>
            {isPending && <Skeleton variant="rounded" sx={styles.skeleton} />}
            <Typography variant="body" as="span" aria-hidden={isPending}>
              {t("billing.uniqueContacts.dataThrough", {
                defaultValue: "Data through: {{date}}",
                // This is a Taiwan reporting date, not an instant to convert locally.
                date: dataThroughDate?.replaceAll("-", "/") ?? "0000/00/00",
              })}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };
